import type { AxiosProgressEvent } from 'axios';
import { getDocumentTypeExt } from 'core/utils/documentUtils';
import { http } from 'core/utils/httpClient';
import type { Document } from 'models/document/document';
import type { GroupedAvailableDocuments } from 'models/document/groupedAvailableDocuments';
import type { DocumentTypeEnum } from 'models/enums/documentTypeEnum';
import type { DocumentResponse } from './dtoModels/document/documentResponse';
import type { GroupedAvailableDocumentsResponse } from './dtoModels/document/groupedAvailableDocumentsResponse';

export async function getDocumentsAsync(): Promise<Document[]> {
  const { data } = await http.get<DocumentResponse[]>(`documents`);

  return data;
}

export async function getAvailableGroupedDocumentsAsync(): Promise<
  GroupedAvailableDocuments[]
> {
  const { data } = await http.get<GroupedAvailableDocumentsResponse>(
    `documents/groups/available`
  );

  return mapGroupedDocuments(data);
}

export async function getDownloadDocumentAsync(
  document: Document,
  orderId: number,
  documentType: DocumentTypeEnum,
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<Blob> {
  const { data } = await http.get<Blob>(
    `documents/${document.id}/download/${getDocumentTypeExt(documentType, false)}`,
    {
      params: { orderId },
      responseType: 'blob',
      onDownloadProgress,
    }
  );

  return data;
}

export function mapGroupedDocuments(
  response: GroupedAvailableDocumentsResponse
): GroupedAvailableDocuments[] {
  const documentMap = new Map<number, DocumentResponse>();
  response.documents.forEach((doc) => documentMap.set(doc.id, doc));

  const usedDocumentIds = new Set<number>();

  const grouped: GroupedAvailableDocuments[] = response.groups.map((group) => {
    const docs: Document[] = group.documentIds
      .map((id) => {
        const doc = documentMap.get(id);
        if (doc) {
          usedDocumentIds.add(id);
          return {
            ...doc,
          };
        }
        return null;
      })
      .filter((d): d is Document => d !== null);

    return {
      id: group.id,
      groupName: group.name,
      documents: docs,
    };
  });

  const ungroupedDocs: Document[] = response.documents
    .filter((doc) => !usedDocumentIds.has(doc.id))
    .map((doc) => ({
      id: doc.id,
      name: doc.name,
      documentTypes: doc.documentTypes,
    }));

  if (ungroupedDocs.length > 0) {
    grouped.unshift({
      id: null,
      groupName: null,
      documents: ungroupedDocs,
    });
  }

  return grouped;
}
